// app/javascript/controllers/give/stripe_fields_controller.js
import { Controller } from "@hotwired/stimulus";
import { loadStripe } from "@stripe/stripe-js";
import Rails from "@rails/ujs";

export default class extends Controller {
  static targets = ["errors", "cardName"];
  static values = {
    publishableKey: String,
    setupIntentUrl: String,
  };

  async connect() {
    this.stripe = await loadStripe(this.publishableKeyValue);
    this.setupStripeElements();
    this.handleSetupIntentRedirect();
  }

  disconnect() {
    if (!this.cardElements) return;
    this.cardElements.number.unmount();
    this.cardElements.expiry.unmount();
    this.cardElements.cvc.unmount();
  }

  // Public method called by form controller
  async tokenize() {
    // Skip if token was already added (e.g., from 3DS redirect-back)
    if (
      this.element.querySelector(
        'input[name="payment_schedule[payment_method_attributes][token]"]',
      )
    ) {
      return true;
    }
    this.clearErrors();

    try {
      // Fetch a SetupIntent client_secret from the server
      const response = await fetch(this.setupIntentUrlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": Rails.csrfToken(),
        },
      });

      if (!response.ok) {
        const data = await response.json();
        this.showError(data.error || "Failed to initialize card setup");
        return false;
      }

      const { client_secret } = await response.json();

      // Confirm the card setup via Stripe — handles SCA/3DS if needed
      const result = await this.stripe.confirmCardSetup(
        client_secret,
        {
          payment_method: {
            card: this.cardElements["number"],
            billing_details: {
              name: this.cardNameTarget.value,
              address: { country: "US" },
            },
          },
        },
        {
          return_url: window.location.href,
        },
      );

      if (result.error) {
        this.showError(result.error.message);
        return false;
      }

      this.addTokenToForm(result.setupIntent.id);
      return true;
    } catch (error) {
      this.showError("Payment processing failed");
      return false;
    }
  }

  // Private methods
  showError(message) {
    if (this.errorsTarget) {
      // scroll error element into view
      this.errorsTarget.scrollIntoView({ behavior: "smooth", block: "center" });

      this.errorsTarget.textContent = message;
      this.errorsTarget.hidden = false;
    }
  }

  clearErrors() {
    if (this.errorsTarget) {
      this.errorsTarget.textContent = "";
      this.errorsTarget.hidden = true;
    }
  }

  addTokenToForm(paymentMethodId) {
    const input = document.createElement("input");
    input.setAttribute("type", "hidden");
    input.setAttribute(
      "name",
      "payment_schedule[payment_method_attributes][token]",
    );
    input.setAttribute("value", paymentMethodId);
    this.element.appendChild(input);
  }

  setupStripeElements() {
    const elements = this.stripe.elements();

    const style = {
      base: {
        fontSize: "1.0625rem",
        lineHeight: "22px",
        padding: "0.5625rem 0.75rem",
      },
    };

    const classes = {
      focus: "focus",
      empty: "empty",
      invalid: "invalid is-invalid",
    };

    this.cardElements = {
      number: elements.create("cardNumber", { style, classes }),
      expiry: elements.create("cardExpiry", { style, classes }),
      cvc: elements.create("cardCvc", { style, classes }),
    };

    Object.values(this.cardElements).forEach((element) => {
      element.on("change", this.handleCardValidation.bind(this));
    });

    this.cardElements.number.mount("#cardNumber");
    this.cardElements.expiry.mount("#cardExpiry");
    this.cardElements.cvc.mount("#cardCvc");
  }

  // Handle redirect-back from 3DS authentication
  async handleSetupIntentRedirect() {
    const params = new URLSearchParams(window.location.search);
    const clientSecret = params.get("setup_intent_client_secret");
    if (!clientSecret) return;

    const { setupIntent, error } =
      await this.stripe.retrieveSetupIntent(clientSecret);
    if (error) {
      this.showError(error.message);
      return;
    }
    if (setupIntent.status === "succeeded") {
      this.addTokenToForm(setupIntent.id);
    } else {
      this.showError("Card setup was not completed. Please try again.");
    }
  }

  // Event Listeners
  handleCardValidation(event) {
    if (event.error) {
      this.showError(event.error.message);
    } else {
      this.clearErrors();
    }
  }
}
